<template>
	<main id="pages-search-search-content" class="ragnarok">
		<a href="#search-results" id="skipFilters" class="screenreader-only">Skip to search results</a>
		<div class="banner" :class="tab">
			<div class="level container has-text-centered-mobile">
				<div class="level-left">
					<div class="level-item">
						<nav class="breadcrumb" aria-label="Navigation breadcrumb">
							<ul>
								<li v-for="breadcrumb in breadcrumbs"><a :href="breadcrumb.url">{{breadcrumb.label}}</a></li>
							</ul>
						</nav>
						<h1 class="title is-2" v-if="bannerText">{{bannerText}}</h1>
						<div class="search-tabs" v-if="!isHomogeneousResults">
							<a tabindex="0" role="button" :aria-label="`View results for ${resultTabText}`" class="button is-rounded" v-for="resultTabText in resultsAvailableFor" :class="[isTabSelected(resultTabText) ? 'is-black' : 'is-outlined']" @click="showResultsFor({tab: resultTabText, skipHistory: false})" @keyup.enter="showResultsFor({tab: resultTabText, skipHistory: false})">
								<i :class="iconFor(resultTabText)"></i> {{resultTabText}}
							</a>
						</div>
					</div>
				</div>
				<div class="level-right is-hidden-touch">
					<div class="level-item">
						<a href="/quick-order">
							<i class="fal fa-rabbit-fast"></i> Quick Order
						</a>
					</div>
				</div>
			</div>
		</div>
		
		<div v-if="isTabSelected('Products')">
			<div id="product-search-results">
				<category-filters></category-filters>
				<search-results-summary></search-results-summary>
				<a name="search-results"></a>
				<search-product-results></search-product-results>
			</div>
		</div>
		<div v-if="isTabSelected('Articles')">
			<a name="search-results"></a>
			<search-article-results></search-article-results>
		</div>
		<div v-if="isTabSelected('Videos')">
			<a name="search-results"></a>
			<search-video-results></search-video-results>
		</div>
		<div v-if="isTabSelected('Resources')">
			<a name="search-results"></a>
			<search-resource-results></search-resource-results>
		</div>
		<div v-if="isTabSelected('Ingredients')">
			<a name="search-results"></a>
			<search-ingredient-results></search-ingredient-results>
		</div>
		<div v-if="isTabSelected('Podcasts')">
			<a name="search-results"></a>
			<search-podcast-results></search-podcast-results>
		</div>
		<div v-if="isTabSelected('Affiliates')">
			<a name="search-results"></a>
			<search-affiliate-results></search-affiliate-results>
		</div>
		
		<div class="no-results has-text-centered" v-if="isEmpty">
			<h4 class="title is-4" tabindex="0" ref="emptyResults">Sorry, we couldn't find any items with that search.</h4>
			<a href="/products">Back to all products</a>
		</div>
	</main>
</template>

<script>
import { mapGetters, mapState, mapActions, mapMutations } from "vuex";

import VueScrollTo from "vue-scrollto";
import ProductSet from "~/components/ProductSet.vue";
import CustomProductTile from "~/components/CustomProductTile.vue";
import MultiselectButton from "~/components/MultiselectButton.vue";
import BlogArticleTile from "~/components/search/BlogArticleTile.vue";
import VideoTile from "~/components/search/VideoTile.vue";
import IngredientTile from "~/components/search/IngredientTile.vue";
import ResourceTile from "~/components/search/ResourceTile.vue";
import ImageCardNew from "~/components/features/ImageCardNew.vue";
import TagSearchInput from "~/components/search/TagSearchInput.vue";
import ShowMore from "~/components/ShowMore.vue";
import MarketingProductTile from "~/components/marketing/MarketingProductTile.vue"
import BoundImage from "~/components/BoundImage.vue";

import CategoryFilters from "~/components/search/CategoryFilters.vue";
import SearchResultsSummary from "~/components/search/SearchResultsSummary.vue";

import SearchArticleResults from "~/components/search/result-sections/SearchArticleResults.vue";
import SearchIngredientResults from "~/components/search/result-sections/SearchIngredientResults.vue";
import SearchProductResults from "~/components/search/result-sections/SearchProductResults.vue";
import SearchResourceResults from "~/components/search/result-sections/SearchResourceResults.vue";
import SearchVideoResults from "~/components/search/result-sections/SearchVideoResults.vue";
import SearchPodcastResults from "~/components/search/result-sections/SearchPodcastResults.vue";
import SearchAffiliateResults from "~/components/search/result-sections/SearchAffiliateResults.vue";

import store from "~/store.js";

const historyAction = {
	PUSH: "push",
	REPLACE: "replace",
	SKIP: "skip"
};

export default {
	name: "SearchResults",
	store,
	components: {
		ProductSet,
		MultiselectButton,
		BlogArticleTile,
		VideoTile,
		IngredientTile,
		ResourceTile,
		ImageCardNew,
		TagSearchInput,
		ShowMore,
		CustomProductTile,
		MarketingProductTile,
		BoundImage,
		CategoryFilters,
		SearchResultsSummary,
		SearchArticleResults,
		SearchIngredientResults,
		SearchProductResults,
		SearchResourceResults,
		SearchVideoResults,
		SearchPodcastResults,
		SearchAffiliateResults
	},
	
	computed: {
		...mapState("search", ["breadcrumbs", "resultsAvailableFor", "tab"]),
		...mapGetters("search", ["isTabSelected", "iconFor", "bannerText", "isHomogeneousResults", "isEmpty"])
	},
	
	methods: {
		...mapMutations("search", ["setPagedLists", "setMarketingTest", "setMostRelevantQuiz"]),
		...mapActions("search", ["bootstrap", "showResultsFor", "initialize"]),
		...mapActions("tgpt", ["setProducts"])
	},
	
	mounted() {
		
		this.bootstrap({
			pagedLists: pagedLists || {},
			searchQuery: searchQuery || "",
			hideFilters: hideFilters || false,
			isDispensary: isDispensary || false,
			isAmbassador: isAmbassador || false,
			mostRelevantQuiz: mostRelevantQuiz || null,
			marketingTest: marketingTest || null,
			backLink: backLink || null
		});
		
		setTimeout(() => {
			if (this.$refs.emptyResults) {
				this.$refs.emptyResults.focus();
			}
		}, 2000);
	},

	created() {
		let _self = this;
		
		// Gracefully handle back/forward w/ all our AJAX
		window.onpopstate = function(event) {
			if(event.state) {
				if(event.state.tab && event.state.tab !== _self.tab) {
					// Navigating to a tab via back/forward
					_self.showResultsFor({tab: event.state.tab, skipHistory: true});
				} else {
					// Navigating to different page or filter option
					const url = new URL(document.location);
					url.searchParams.set("format", "json");
					
					axios.get(url.toString())
						.then((response) => {
							_self.setPagedLists(response.data.page);
							_self.setMarketingTest(response.data.mostRelevantTest);
							_self.setMostRelevantQuiz(response.data.mostRelevantQuiz);
							_self.initialize(historyAction.SKIP);
							VueScrollTo.scrollTo("main");
						});
				}
			}
		};
		const productsObj = {
			givenPageType:'PLP',
			data: pagedLists.products.data || []
		}
		this.setProducts(productsObj)
	}
}
</script>

<style lang="scss" scoped>
	// To do after Nuxt, do not have variables and mixins accessible
</style>